import cx from 'classnames';
import type { CSSProperties, FunctionComponent, MouseEvent, ReactNode } from 'react';

import { defineSlotComponent, findFirstForSlot } from '../../layouts/slots';
import type { SlotComponent } from '../../layouts/slots';

import FileTreeNodeArrow, { type FileTreeNodeArrowProps } from './FileTreeNodeArrow';
import FileTreeNodeIcon, { type FileTreeNodeIconProps } from './FileTreeNodeIcon';
import FileTreeNodeText, { type FileTreeNodeTextProps } from './FileTreeNodeText';

import styles from './FileTree.module.less';

type Props = {
  /**
   * style prop (provided by react-arborist)
   */
  style: CSSProperties;
  /**
   * Event handler for when the node is clicked.
   *
   * @param {MouseEvent<HTMLButtonElement>} event - The event object.
   */
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Whether the node is selected (comes from react-arborist via node.isSelected)
   */
  isSelected: boolean;
  /**
   * Is this node a directory or a file?
   */
  isDirectory?: boolean;
  /**
   * Callback for when the mouse enters the node.
   * This is helpful for hooking into useOnHover.
   */
  onMouseEnter?: () => void;
  /**
   * Callback for when the mouse leaves the node.
   * This is helpful for hooking into useOnHover.
   */
  onMouseLeave?: () => void;
};

type FileTreeNodeComponent = FunctionComponent<Props> & {
  Arrow: SlotComponent<FileTreeNodeArrowProps>;
  Icon: SlotComponent<FileTreeNodeIconProps>;
  Text: SlotComponent<FileTreeNodeTextProps>;
  Rest: SlotComponent<{ children: ReactNode }>;
};

/**
 * This component can act as a "wrapper" for the Node component you would pass to Tree
 * from react-arborist. Many of the props come from whatever `node` prop you are using.
 *
 * @example
 * const MyFileTreeNode: VoidFunctionComponent<NodeRendererProps<MyData>> = ({ node, style }) => {
 *   return (
 *     <FileTreeNode style={style} onClick={onClick} isSelected={node.isSelected}>
 *       <FileTreeNode.Arrow isOpen={node.isOpen} isDirectory={node.data.type === 'directory'} />
 *       <FileTreeNode.Icon isDirectory={node.data.type === 'directory'} />
 *       <FileTreeNode.Text text={node.data.name} />
 *       <FileTreeNode.Rest>
 *         any other content you want to add to the node
 *       </FileTreeNode.Rest>
 *     </FileTreeNode>
 *   );
 * };
 *
 * // In your Tree component
 * <Tree {...props}>
 *   {MyFileTreeNode}
 * </Tree>
 */
const FileTreeNode: FileTreeNodeComponent = ({
  children,
  style,
  onClick,
  isSelected,
  isDirectory,
  onMouseEnter,
  onMouseLeave,
}) => {
  const Arrow = findFirstForSlot('Arrow', children);
  const Icon = findFirstForSlot('Icon', children);
  const Text = findFirstForSlot('Text', children);
  const Rest = findFirstForSlot('Rest', children);
  return (
    <button
      type="button"
      style={style}
      tabIndex={0}
      className={cx(styles.treeViewNode, {
        [styles.treeViewNodeSelected]: isSelected,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={cx(styles.treeViewNodeInner, {
        [styles.treeViewNodeInnerBorder]: !isDirectory,
      })}>
        <span className={styles.treeViewNodeContent}>
          {isDirectory ? Arrow : null}
          {!isDirectory ? Icon : null}
          {Text}
        </span>
        {Rest}
      </div>
    </button>
  );
};

FileTreeNode.Arrow = defineSlotComponent('Arrow', FileTreeNodeArrow);
FileTreeNode.Icon = defineSlotComponent('Icon', FileTreeNodeIcon);
FileTreeNode.Text = defineSlotComponent('Text', FileTreeNodeText);
//  Allows you to pass the rest of the props.
FileTreeNode.Rest = defineSlotComponent('Rest', ({ children }) => <>{children}</>); // eslint-disable-line react/jsx-no-useless-fragment

export default FileTreeNode;
